import React, { useRef, useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { AppChrome } from '../components/alt/AppChrome';
import { ChatsScreen, SharedScreen, WorkflowsScreen, PulsesScreen, SkillsScreen } from '../components/alt/PlatformPillar';
import { ConnectionsScreen } from '../components/alt/ConnectedAppsPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { ConnectedAppsTable } from './Alt';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal } from './CompanyBrain';
import { ChatGPTPanel } from '../components/alt/ChatGPTPanel';
import { domToSvg } from '../lib/domToSvg';

/**
 * Export surface for the product screens used in the Workspace section of the
 * home page.
 *
 * These are vectorised, not screenshotted: the DOM is walked and re-emitted as
 * SVG primitives, so what lands in Figma is real rectangles, real lines and
 * real text layers you can retype and restyle — not a flat picture.
 */

type Screen = {
  id: string;
  name: string;
  note: string;
  section: string;
  /** Product screens sit in app chrome; diagrams and panels stand alone. */
  chrome?: boolean;
  /** Screen already carries its own inner padding. */
  selfPadded?: boolean;
  active?: string;
  /** Screens that are the whole view — no app nav beside them. */
  noSidebar?: boolean;
  /** Wider canvas for the full-bleed diagrams. */
  wide?: boolean;
  render: () => React.ReactNode;
};
const SCREENS: Screen[] = [
// ── Connected Apps ───────────────────────────────────────────────────────
{
  id: 'connections',
  name: 'Connections',
  note: 'Tools, accounts and the AI surfaces that reach them',
  section: 'Connected Apps',
  chrome: true,
  selfPadded: true,
  active: 'Connections',
  wide: true,
  render: () => <ConnectionsScreen data-id="element-8725" data-mp="cnu72d8fescw" />
}, {
  id: 'comparison',
  name: 'Connection comparison',
  note: 'Giga vs ChatGPT vs Claude',
  section: 'Connected Apps',
  render: () => <ConnectedAppsTable data-id="element-8726" data-mp="264ytgf1rc714" />
},
// ── Company Brain ────────────────────────────────────────────────────────
{
  id: 'brain-flow',
  name: 'Sources into tracks',
  note: 'Observations flowing from scans and chats',
  section: 'Company Brain',
  wide: true,
  render: () => <BrainFlowExperiment data-id="element-8727" data-mp="2188fpydfdniv" />
},
// ── AI Co Worker ─────────────────────────────────────────────────────────
{
  id: 'slack',
  name: 'Slack thread',
  note: 'Giga answering in #sales',
  section: 'AI Co Worker',
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="slack" showPlatformToggle={false} data-id="element-8728" data-mp="2tvcehllxd5m7" />
}, {
  // Teams is its own frame rather than a toggle inside the Slack one, so it
  // can be exported and reviewed on its own terms.
  id: 'teams',
  name: 'Teams chat',
  note: 'Giga answering in Microsoft Teams',
  section: 'AI Co Worker',
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="teams" showPlatformToggle={false} data-id="element-8729" data-mp="19gk6vltuvj4s" />
}, {
  id: 'claude-code',
  name: 'Claude Code terminal',
  note: 'Giga over MCP',
  section: 'AI Co Worker',
  render: () => <CodeTerminal data-id="element-8730" data-mp="wwn5p4paqzkg" />
}, {
  id: 'chatgpt',
  name: 'ChatGPT surface',
  note: 'Giga answering inside ChatGPT',
  section: 'AI Co Worker',
  render: () => <ChatGPTPanel heightClass="h-[560px]" data-id="element-8731" data-mp="15y3i1hzo9brb" />
},
// ── Workspace ────────────────────────────────────────────────────────────
{
  id: 'chats',
  name: 'AI chats',
  note: 'Renewal risk in Salesforce',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  selfPadded: true,
  render: () => <ChatsScreen data-id="element-8732" data-mp="d7l6rtoso42t" />
}, {
  id: 'groups',
  name: 'Team workspaces',
  note: 'Groups',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <SharedScreen data-id="element-8733" data-mp="n6tva70w4qhs" />
}, {
  id: 'workflows',
  name: 'Workflows',
  note: 'Monday morning update',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <WorkflowsScreen data-id="element-8734" data-mp="3qou8c0lazm29" />
}, {
  id: 'pulses',
  name: 'Pulses',
  note: '4 enabled',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <PulsesScreen data-id="element-8735" data-mp="2tys38o247975" />
}, {
  id: 'skills',
  name: 'Skills',
  note: 'Recorded once, reused everywhere',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <SkillsScreen data-id="element-8736" data-mp="32wtz6p6zrbnk" />
},
// ── Architecture ─────────────────────────────────────────────────────────
{
  id: 'architecture',
  name: 'Architecture',
  note: 'Ways to use Giga',
  section: 'Architecture',
  wide: true,
  render: () => <ArchitectureWidget data-id="element-8737" data-mp="3cpj9hgqt2jhb" />
}];
function downloadSvg(markup: string, filename: string) {
  const url = URL.createObjectURL(new Blob([markup], {
    type: 'image/svg+xml'
  }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
export function ExportDashboards() {
  const frames = useRef<Record<string, HTMLDivElement | null>>({});
  const [copied, setCopied] = useState<string | null>(null);
  const capture = (id: string) => {
    const node = frames.current[id];
    return node ? domToSvg(node) : null;
  };
  const copy = async (id: string) => {
    const markup = capture(id);
    if (!markup) return;
    await navigator.clipboard.writeText(markup);
    setCopied(id);
    setTimeout(() => setCopied(c => c === id ? null : c), 2000);
  };
  const save = (id: string) => {
    const markup = capture(id);
    if (markup) downloadSvg(markup, `giga-${id}.svg`);
  };
  return <main className="min-h-screen w-full bg-[#F4F1EA] pb-24" data-id="element-8738" data-mp="27os2tozucfg1">
      <header className="border-b border-[#E7E0D4] bg-[#FAF8F4]" data-id="element-8739" data-mp="27m5uhn54lvwr">
        <div className="mx-auto max-w-[1240px] px-6 py-10" data-id="element-8740" data-mp="22pyojucqcket">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-8741" data-mp="1ync1e6r07nny">
            Export
          </p>
          <h1 className="mt-3 font-title text-3xl font-semibold tracking-tight text-[#2B211A] md:text-[40px]" data-id="element-8742" data-mp="1tnz56ve8t48p">
            Home page screens
          </h1>
          <p className="mt-3 max-w-[64ch] text-[15.5px] leading-relaxed text-[#6B5D52]" data-id="element-8743" data-mp="i9lme4ycjezj">
            Every product screen and diagram shown on the home page, across all
            four pillars, vectorised into editable layers. Copy one, paste into
            Figma, and you get real rectangles and real text you can retype —
            not a screenshot.
          </p>

          <ol className="mt-6 max-w-[64ch] space-y-2 rounded-xl border border-[#E7E0D4] bg-white px-5 py-4 text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-8744" data-mp="as5k1s4d82j0">
            <li data-id="element-8745" data-mp="zzs2ie45l3t">
              <strong className="font-semibold text-[#2B211A]" data-id="element-8746" data-mp="zhot1t1a6me">1.</strong> Keep
              this window at desktop width — the app sidebar only renders above
              1024px, and the export captures what is actually on screen.
            </li>
            <li data-id="element-8747" data-mp="3s6kfzt8vgcwm">
              <strong className="font-semibold text-[#2B211A]" data-id="element-8748" data-mp="3s61875cxr4wb">2.</strong> Hit
              Copy, then paste directly onto a Figma canvas.
            </li>
            <li data-id="element-8749" data-mp="3e7l0bb3c78cl">
              <strong className="font-semibold text-[#2B211A]" data-id="element-8750" data-mp="3e95nugr8hr0y">3.</strong>{' '}
              Install DM Sans and Inter in Figma so the type matches. Everything
              arrives as live text layers in those families.
            </li>
          </ol>

          <p className="mt-4 max-w-[64ch] text-[12.5px] leading-relaxed text-neutral-500" data-id="element-8751" data-mp="2umb4hkwyevn1">
            Two things SVG cannot carry: drop shadows and the frame's outer
            elevation are dropped rather than faked, and brand logos come across
            as linked images rather than vectors. The animated screens — the
            Slack thread and the brain flow — export in whatever state they are
            in when you hit Copy, so pause on the frame you want.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-[1360px] space-y-14 px-6 pt-14" data-id="element-8752" data-mp="3s88ib50e2csn">
        {SCREENS.map((s, i) => {
        const copiedNow = copied === s.id;
        const newSection = i === 0 || SCREENS[i - 1].section !== s.section;
        return <section key={s.id} data-id="element-8753" data-mp="51pkd6206pza" data-mpi={i}>
              {/* Grouped by the pillar the screen belongs to, so the page
               matches the order you scroll past on the home page. */}
              {newSection && <p className="mb-5 border-b border-[#E7E0D4] pb-2 text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-8754" data-mp="17jr9x7i9qgdh">
                  {s.section}
                </p>}
              <div className="mb-4 flex flex-wrap items-end justify-between gap-4" data-id="element-8755" data-mp="1giw5q62g3vly">
                <div data-id="element-8756" data-mp="1lgny11705jqo">
                  <h2 className="font-title text-[22px] font-semibold tracking-tight text-[#2B211A]" data-id="element-8757" data-mp="1lf3e05ibdj72">
                    {s.name}
                  </h2>
                  <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-8758" data-mp="stk17ji2cbpz">
                    {s.note} · giga-{s.id}.svg
                  </p>
                </div>
                <div className="relative z-10 flex flex-wrap items-center gap-2" data-id="element-8759" data-mp="12ibmx9wj5zsg">
                  <button type="button" onClick={() => copy(s.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-8760" data-mp="12keomr71phd0">
                    {copiedNow ? <Check size={13} className="text-emerald-400" data-id="element-8761" data-mp="2i9nw9hxdwkn" /> : <Copy size={13} data-id="element-8762" data-mp="2hqdreyioch6" />}
                    {copiedNow ? 'Copied — paste in Figma' : 'Copy for Figma'}
                  </button>
                  <button type="button" onClick={() => save(s.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-8763" data-mp="3opg8qv1lfzc5">
                    <Download size={13} data-id="element-8764" data-mp="3fqmhlk71815z" />
                    Download SVG
                  </button>
                </div>
              </div>

              {/* The captured node. Padding sits inside it so the exported
               artboard carries its own margin around the frame.
                `relative isolate` is load-bearing: these screens come from the
               home page, where they sit inside positioned sections. Rendered
               bare here, any decorative `absolute inset-0` layer inside them
               would resolve against the initial containing block — painting a
               viewport-sized panel over the top of the page and swallowing
               clicks on the export buttons. Positioning each frame keeps every
               overlay inside the screen it belongs to. */}
              <div ref={el => {
            frames.current[s.id] = el;
          }} className={`relative isolate overflow-x-auto rounded-2xl bg-white p-10 ${s.wide ? '' : 'mx-auto max-w-[900px]'}`} data-id="element-8765" data-mp="5tneb9sipoek">
                {s.chrome ? <AppChrome active={s.active} sidebar={!s.noSidebar} data-id="element-8766" data-mp="39a48d4zw3utk">
                    {s.selfPadded ? s.render() : <div className="px-5 py-5 md:px-7 md:py-6" data-id="element-8767" data-mp="34bkk2xxtq6y1">
                        {s.render()}
                      </div>}
                  </AppChrome> : s.render()}
              </div>
            </section>;
      })}
      </div>
    </main>;
}